import React, { useState, useEffect } from "react";
import { Alert, Card, Col, Container, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "../../Common/Breadcrumbs";
import ShimmerLoader from "../../Common/ShimmerLoader";
import Pagination from "../ShopServices/Orders/components/Pagination";
import { DeleteModal } from "../../Common/DeleteModal";
import { useDebounce } from "../../hooks/useDebounce";
import CourseAssignmentsTable from "../../components/academyCourseAssignments/organisms/CourseAssignmentsTable";
import CourseAssignmentDrawer from "../../components/academyCourseAssignments/organisms/CourseAssignmentDrawer";
import { useCourseAssignments } from "./useCourseAssignments";
import "../Ads/Creatives/Creatives.css";
import "./CourseAssignments.css";

const CourseAssignmentsPage: React.FC = () => {
  const navigate = useNavigate();
  const { functions, values, loading, error, setters } = useCourseAssignments();

  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 300);

  useEffect(() => {
    setters.setSearch(debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  return (
    <div className="page-content">
      <Container fluid>
        <div className="mb-3" style={{ padding: "0 15px" }}>
          <Breadcrumbs
            items={[
              { label: "Academy", onClick: () => navigate("/academy-enrolments") },
              { label: "Course Assignments", active: true },
            ]}
          />
        </div>

        <div className="mb-4" style={{ padding: "0 15px" }}>
          <h1 className="ads-page-title">Course Assignments</h1>
          <p className="ads-page-subtitle">Total {values.totalCount} Course Assignment{values.totalCount !== 1 ? "s" : ""}</p>
        </div>

        <Row style={{ padding: "0 15px" }}>
          <Col>
            <Card className="ads-page-card">
              <Card.Body className="ads-page-card__body ca-page-card__body">
                {/* Toolbar */}
                <div className="ads-toolbar">
                  <div className="ads-searchbar">
                    <div className="ads-searchbar__icon">
                      <i className="ph-magnifying-glass" />
                    </div>
                    <input
                      type="text"
                      className="ads-searchbar__input"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      placeholder="Search by phone number"
                      aria-label="Search course assignments"
                    />
                    {searchInput && (
                      <button
                        type="button"
                        className="ads-searchbar__clear"
                        onClick={() => setSearchInput("")}
                        aria-label="Clear search"
                      >
                        <i className="ph-x" />
                      </button>
                    )}
                  </div>

                  <div className="ca-toolbar-right">
                    <div className="ca-status-filter">
                      <label className="ca-status-filter__label" htmlFor="ca-status-select">
                        Filter by status
                      </label>
                      <select
                        id="ca-status-select"
                        className="ca-status-filter__select"
                        value={values.status}
                        onChange={(e) => setters.setStatus(e.target.value)}
                      >
                        <option value="">All</option>
                        <option value="ASSIGNED">Assigned</option>
                        <option value="REVOKED">Revoked</option>
                      </select>
                    </div>

                    <button
                      type="button"
                      className="ads-create-btn"
                      onClick={functions.handleCreate}
                    >
                      <i className="ph-plus" />
                      Create Assignment
                    </button>
                  </div>
                </div>

                {/* Content */}
                {loading.isPageLoading ? (
                  <ShimmerLoader type="table" rows={6} />
                ) : values.assignments.length > 0 ? (
                  <CourseAssignmentsTable
                    assignments={values.assignments}
                    onChangeStatus={functions.handleChangeStatus}
                    onRequestRevoke={functions.handleRequestRevoke}
                  />
                ) : (
                  <div className="ads-empty">
                    <p className="ads-empty__text">No course assignments found.</p>
                  </div>
                )}
              </Card.Body>
            </Card>

            {!loading.isPageLoading && values.totalCount > 0 && values.totalPages > 1 && (
              <Pagination
                currentPage={values.page}
                totalPages={values.totalPages}
                totalCount={values.totalCount}
                pageSize={values.pageSize}
                onPageChange={(p) => setters.setPage(p)}
                onPageSizeChange={(size) => {
                  setters.setPageSize(size);
                  setters.setPage(1);
                }}
              />
            )}
          </Col>
        </Row>

        {error && (
          <div style={{ padding: "0 15px", marginTop: 16 }}>
            <Alert variant="danger">{error}</Alert>
          </div>
        )}
      </Container>

      {/* Create assignment drawer */}
      <CourseAssignmentDrawer
        show={values.drawerOpen}
        onHide={functions.handleDrawerHide}
        onSuccess={functions.handleDrawerSuccess}
      />

      {/* Revoke confirmation */}
      <DeleteModal
        show={values.revokeTarget !== null}
        handleClose={functions.handleCancelRevoke}
        deleteModalFunction={functions.handleConfirmRevoke}
        showIcon={false}
        title="Revoke this assignment?"
        message="The user will lose access to this course. This action can be reversed by assigning again."
        confirmLabel="Revoke"
        cancelLabel="Cancel"
        isLoading={values.isRevoking}
        error={values.revokeError}
      />
    </div>
  );
};

export default CourseAssignmentsPage;
